import { EncounterScene } from "../scenes/EncounterScene";
import { BossPromptScreen } from "../scenes/EncounterScene/screens/BossPromptScreen";
import { DefeatScreen } from "../scenes/EncounterScene/screens/DefeatScreen";
import { EncounterScreen } from "../scenes/EncounterScene/screens/EncounterScreen";
import { RewardScreen } from "../scenes/EncounterScene/screens/RewardScreen";
import { RestScreen } from "../scenes/EncounterScene/screens/RestScreen";
import { RunVictoryScreen } from "../scenes/EncounterScene/screens/RunVictoryScreen";
import { VictoryScreen } from "../scenes/EncounterScene/screens/VictoryScreen";
import { Screen, ScreenContext, ScreenKey } from "./types";

type ScreenFactory = (scene: EncounterScene) => Screen;
type ScreenFactoryMap = Partial<Record<ScreenKey, ScreenFactory>>;

export class ScreenManager {
  private currentScreen?: Screen;
  private factories: ScreenFactoryMap;

  constructor(
    private readonly scene: EncounterScene,
    private readonly uiLayer: Phaser.GameObjects.Container,
    extraFactories?: ScreenFactoryMap
  ) {
    this.factories = {
      ENCOUNTER: (s) => new EncounterScreen(s),
      VICTORY: (s) => new VictoryScreen(s),
      RUN_VICTORY: (s) => new RunVictoryScreen(s),
      DEFEAT: (s) => new DefeatScreen(s),
      BOSS_PROMPT: (s) => new BossPromptScreen(s),
      REWARD: (s) => new RewardScreen(s),
      REST: (s) => new RestScreen(s),
      ...extraFactories,
    };
  }

  /**
   * Register or override a screen factory. Call before setScreen for new keys.
   */
  register(key: ScreenKey, factory: ScreenFactory) {
    this.factories[key] = factory;
  }

  /**
   * Register multiple screen factories at once (useful for scene-local bundles).
   */
  registerMany(factories: ScreenFactoryMap) {
    Object.entries(factories).forEach(([key, factory]) => {
      if (factory) {
        this.register(key as ScreenKey, factory);
      }
    });
  }

  setScreen(key: ScreenKey, context?: ScreenContext) {
    if (this.currentScreen) {
      this.currentScreen.exit();
    }

    const screen = this.createScreen(key);
    this.currentScreen = screen;

    // Default to UI layer; screens can move objects to worldLayer if needed.
    this.uiLayer.add(screen.container);
    screen.enter(context);
  }

  private createScreen(key: ScreenKey): Screen {
    const factory = this.factories[key];
    if (!factory) {
      throw new Error(`No screen factory registered for key ${key}`);
    }
    return factory(this.scene);
  }
}
